import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { isDevEnv, isHomologEnv } from 'utils/env';
import { createSanitizeHooks } from 'lib/sentrySanitize';
import { createReplayIntegrations, stopReplay } from 'lib/sentryReplay';

// Ponto único de sanitização (SAT-sentry): TUDO que sai para o Sentry passa por estes hooks.
// Se o sanitizador falhar, o original é descartado e vai um evento mínimo fixo (sem laço: o evento mínimo é literal).
const sanitizeHooks = createSanitizeHooks(
  (kind, error) => {
    Sentry.captureMessage('sentry_sanitize_failed', {
      level: 'error',
      tags: { sanitize_failed_for: kind },
      extra: { sanitizerError: error instanceof Error ? error.name : typeof error },
    });
  },
  // navegação interna para rota sensível: para o replay (o Meta do rrweb não passa por hook nenhum)
  stopReplay,
);

// Disable console log on production and homolog
//  pending see why homolog returning production
// Change before Sentry, allowing Sentry to log console messages but not show on the console ouput.
if (!isDevEnv && !isHomologEnv) {
  window.console = {
    ...window.console,
    info: () => {},
    log: () => {},
    warn: () => {},
  };
}

Sentry.init({
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: import.meta.env.VITE_VERSION,
  integrations: [
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    ...createReplayIntegrations(window.location.href, sanitizeHooks.beforeAddRecordingEvent),
  ],
  beforeSend: sanitizeHooks.beforeSend,
  beforeSendTransaction: sanitizeHooks.beforeSendTransaction,
  beforeBreadcrumb: sanitizeHooks.beforeBreadcrumb,
  beforeSendLog: sanitizeHooks.beforeSendLog,
  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: import.meta.env.VITE_SENTRY_ENVIRONMENT === 'Dev' ? 0.4 : 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/.*\.lkp\.app\.br/, /^https:\/\/.*\.lkp\.dev\.br/],
  // debug: import.meta.env.VITE_SENTRY_ENVIRONMENT === 'Dev',
  replaysOnErrorSampleRate: import.meta.env.VITE_SENTRY_ENVIRONMENT === 'Dev' ? 1.0 : 0.4,
  replaysSessionSampleRate: import.meta.env.VITE_SENTRY_ENVIRONMENT === 'Dev' ? 1.0 : 0.3,
  _experiments: { enableLogs: true },
});

// replay_event.urls (metadados do replay) só passa por event processors, não por beforeSend.
Sentry.addEventProcessor(sanitizeHooks.processReplayEvent);
